<template lang="pug">
  .page-game
    .inner(ref="inner")
      Pause.pause-view(v-if="isShowPause" @resume="resume")
      Retry.retry(v-if="isShowRetry" @onRetry="retry")
      Present.present(
        v-if="isShowPresent"
        @onRetry="retry"
        @showApply="isShowApply = true; isShowPresent = false;"
      )
      Apply.apply(v-if="isShowApply")
      Results.results(
        v-if="isShowResults"
        :isHighScore="isHighScore"
        @onRetry="retry"
        @showPresent="isShowPresent = true; isShowResults = false"
        @debugScore="debugScore"
      )
      Score(v-if="!isShowResults" ref="score" @toggleDebug="isShowDebug = !isShowDebug")
      Life(ref="life")
      button.button-pause(@click="pause"): img(src="@/assets/images/game/button_pause.png")
      .debug-buttons(v-if="isShowDebug")
        button(@click="debugCure") ハート回復
        button(@click="debugHit") ダメージ
        button(@click="debugAbsorb") ポシェット獲得
        button(@click="debugPowder250") パウダー250獲得
        button(@click="debugFinish") ゴール
      Animation(ref="animation")
      .game-view(ref="gameView")
        .game-header
        .game-container(ref="container")
</template>

<script>
import _ from 'lodash';
import { TweenMax } from 'gsap';
import lottie from 'lottie-web';
import * as PIXI from 'pixi.js';
import { mapActions, mapGetters, mapState } from 'vuex';

import CookieData from '@/assets/js/CookieData';
import SoundController from '@/assets/js/SoundController';
import Utils from '@/assets/js/Utils';
import PreloadAnimation from '@/assets/js/data/preloadAnimation';
import PreloadImages from '@/assets/js/data/preloadImages';
import GameManager from '@/assets/js/game/GameManager';

import Apply from '@/components/Apply';
import Animation from '@/components/Animation';
import Life from '@/components/Life';
import Pause from '@/components/Pause';
import Present from '@/components/Present';
import Retry from '@/components/Retry';
import Results from '@/components/Results';
import Score from '@/components/Score';

export default {
  name: 'Game',
  components: {
    Apply,
    Animation,
    Life,
    Pause,
    Present,
    Retry,
    Results,
    Score
  },
  data() {
    return {
      isHighScore: false,
      isShowApply: false,
      isShowPause: false,
      isShowPresent: false,
      isShowRetry: false,
      isShowResults: false,
      isShowDebug: true
    };
  },
  computed: {
    ...mapGetters(['totalScore']),
    ...mapState(['score', 'life'])
  },
  mounted() {
    this.resetScore();
    this.resetLife();
    this.$refs.animation.load(() => {
      this.$refs.life.load().then(() => {
        PreloadImages.load().then(() => {
          GameManager.init(this.$refs.container, this);
        });
      });
    });
  },
  beforeDestroy() {
    this.resetScore();
    this.resetLife();
    Utils.removeAllTimers();
    SoundController.stopAll();
    GameManager.beforeDestroy();
  },
  methods: {
    ...mapActions([
      'setScore',
      'addScore',
      'resetScore',
      'upLife',
      'downLife',
      'resetLife'
    ]),
    start() {
      this.$refs.inner.classList.add('is-show');
      const onStart = () => {
        let isStandby = false;
        const onComplete = () => {
          SoundController.play('se_shot_handgun');
          SoundController.play('bgm_game');
          GameManager.start();
        };
        const onProcess = bodymovin => {
          if (!isStandby && bodymovin.currentFrame >= 105) {
            isStandby = true;
            GameManager.standby();
          }
        };
        SoundController.play('se_start_countdown');
        this.$refs.animation.play('countDown', onComplete, onProcess);
      };

      Utils.addTimer({ key: 'start', time: 1000 }, onStart);
    },
    retry() {
      const onHide = () => {
        this.$refs.inner.classList.remove('is-show');
        this.$refs.gameView.classList.remove('is-hide');
        this.$refs.life.reset();
        this.$refs.score.reset();

        this.resetScore();
        this.resetLife();
        this.isHighScore = false;
        this.isShowApply = false;
        this.isShowPause = false;
        this.isShowPresent = false;
        this.isShowRetry = false;

        SoundController.stopAll();
        GameManager.reset();
        Utils.addTimer({ key: 'retry', time: 1000 }, this.start);
      };

      this.isShowResults = false;
      Utils.addTimer({ key: 'hideResults', time: 500 }, onHide);
    },
    powder250(onComplete) {
      this.$refs.animation.play('powder250', onComplete);
    },
    gameOver() {
      SoundController.stopAll();
      SoundController.play('jingle_game_over');
      const onComplete = () => (this.isShowRetry = true);
      const onProcess = bodymovin => {
        if (bodymovin.currentFrame >= 45) onComplete();
      };
      this.$refs.animation.play('gameOver', onComplete, onProcess);
    },
    goal() {
      this.isHighScore = this.totalScore > CookieData.highScore;
      CookieData.setHighScore(this.totalScore);
      CookieData.addTotalHappyTurn(this.score);

      const onGoalContentHide = () => {
        this.$refs.gameView.classList.add('is-hide');
      };
      const onStartGoal = () => {
        this.$refs.animation.play('goal', animation => {
          this.isShowResults = true;
        });
      };

      Utils.addTimer({ key: 'goalContentHide', time: 200 }, onGoalContentHide);
      Utils.addTimer({ key: 'startGoal', time: 700 }, onStartGoal);
    },
    addLife() {
      this.$refs.life.up();
      this.upLife();
    },
    removeLife() {
      this.$refs.life.down();
      this.downLife();
    },
    updateScore(score) {
      this.setScore(score);
    },
    pause() {
      if (!GameManager.isStart) return;
      SoundController.play('se_click_button');
      GameManager.pause();
      this.isShowPause = true;
    },
    resume() {
      GameManager.resume();
      this.isShowPause = false;
    },
    debugScore(score) {
      this.setScore(score);
      this.resetLife(1);
    },
    debugFinish() {
      GameManager.debugFinish();
    },
    debugPowder250() {
      GameManager.powder250();
    },
    debugAbsorb() {
      GameManager.absorb();
    },
    debugCure() {
      GameManager.addLife();
    },
    debugHit() {
      GameManager.hit();
    }
  }
};
</script>

<style scoped lang="scss">
.page-game {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000;
  .inner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity 0.5s ease-in-out;
    &.is-show {
      opacity: 1;
    }
  }
  .debug-buttons {
    position: absolute;
    top: vw(250);
    left: vw(30);
    z-index: 1;
  }
  .button-pause {
    position: absolute;
    top: vw(30);
    right: vw(30);
    width: vw(180);
    z-index: 1;
  }
  .pause-view,
  .present,
  .apply,
  .retry,
  .results {
    z-index: 2;
  }
  .game-view {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #adce26;
    transition: opacity 0.5s ease-in-out;
    &.is-hide {
      opacity: 0;
    }
    .game-header {
      height: vw(240);
      background-color: #5fd0fc;
    }
    .game-container {
      position: absolute;
      top: vw(240);
      left: 0;
      width: 100%;
      height: 0;
      padding-bottom: 125%;
    }
  }
}
</style>
